import { useEffect, useMemo, useRef } from "react";
import cn from "classnames";
import { AirStatusesDTO, AirStatusesModel, airStatusLabelMap } from "entities/shared/airCard.types";
import { SeaStatusesModel, seaStatusLabelMap } from "entities/shared/seaCard.types";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import {
  AirFilterType,
  FilterContextType,
  SeaFilterType,
  SetAirFilterFunc,
  SetSeaFilterFunc,
} from "features/shared/filter.types";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import {
  AIR_QUERY_CACHE_KEY,
  airSavedCardStore,
  CountByAirStatusModel,
  useAirInfiniteScrollFetch,
} from "entities/AirSavedCard";
import { searchFilterStore } from "entities/SearchFilter";
import { CountBySeaStatusModel, SEA_QUERY_CACHE_KEY, useSeaInfiniteScrollFetch } from "entities/SeaSavedCard";
import { seaSavedCardStore } from "entities/SeaSavedCard";
import { isSeaStatusModel, ShipmentTypeChecker } from "entities/shared/utils";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { ValueOf } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { Button } from "shared/ui/Button";
import styles from "./StatusFilter.module.scss";

import { useAirStatusFilter } from "../model/useAirStatusFilter";
import { useSeaStatusFilter } from "../model/useSeaStatusFilter";
type StatusFilterProps = {
  filter: SeaFilterType | AirFilterType;
  setFilter: SetSeaFilterFunc | SetAirFilterFunc;
  innerFilterState: FilterContextType["innerFilterState"];
  resetFilter: () => void;
  setFilteredCardAction: (card: ShipmentModel[]) => {
    type: string;
    payload: ShipmentModel[];
  };
  isAirFilter?: (filter: SeaFilterType | AirFilterType) => filter is AirFilterType;
  isSeaFilter?: (filter: SeaFilterType | AirFilterType) => filter is SeaFilterType;
  statusFilter: FilterContextType["statusFilter"];
  setStatusFilter: FilterContextType["setStatusFilter"];
  allStatuses: CountBySeaStatusModel[] | CountByAirStatusModel[];
  setAllStatuses: (statuses: CountBySeaStatusModel[] | CountByAirStatusModel[]) => void;
};

export const StatusFilter: RFC<StatusFilterProps> = ({
  setFilter,
  innerFilterState,
  resetFilter,
  setFilteredCardAction,
  isAirFilter,
  isSeaFilter,
  filter,
  statusFilter,
  setStatusFilter,
  allStatuses,
  setAllStatuses,
}) => {
  const dispatch = useAppDispatch();
  const { showMainView, isCurrentViewOpenedCard, isCurrentViewFilteredCards, currentView } = usePanelViews();
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const { t } = useTranslation();

  const { refetchSeaCards } = useSeaInfiniteScrollFetch({
    queryKey: [SEA_QUERY_CACHE_KEY],
    shouldRequestOnMount: false,
    onSuccess: ({ page, countByStatus }) => {
      dispatch(seaSavedCardStore.actions.appendSavedCardsAction({ data: page }));
      setAllStatuses(countByStatus);
    },
  });

  const { refetchAirCards } = useAirInfiniteScrollFetch({
    queryKey: [AIR_QUERY_CACHE_KEY],
    shouldRequestOnMount: false,
    onSuccess: ({ page, countByStatus }) => {
      dispatch(airSavedCardStore.actions.appendSavedCardsAction({ data: page }));
      setAllStatuses(countByStatus);
    },
  });

  const { seaHandleFilter, seaStatuses } = useSeaStatusFilter({
    ...(isSeaFilter?.(filter) && { setFilter: setFilter as SetSeaFilterFunc }),
    innerFilterState,
    isSeaFilter,
  });
  const { airHandleFilter, airStatuses } = useAirStatusFilter({
    ...(isAirFilter?.(filter) && { setFilter: setFilter as SetAirFilterFunc }),
    innerFilterState,
    isAirFilter,
  });

  const allCardsLength = useMemo(() => {
    return allStatuses.reduce((acc, status) => acc + status.count, 0);
  }, [allStatuses]);

  useEffect(() => {
    if (ShipmentTypeChecker.isSea(shipment)) {
      if (seaStatuses.length) {
        setAllStatuses(seaStatuses);
      }
    }
    if (ShipmentTypeChecker.isAir(shipment)) {
      if (airStatuses.length) {
        setAllStatuses(airStatuses);
      }
    }
  }, [shipment, seaStatuses, airStatuses]);

  const isFirstShipmentRender = useRef(true);

  useEffect(() => {
    if (isFirstShipmentRender.current) {
      isFirstShipmentRender.current = false;
      return;
    }
    showMainView();
    setAllStatuses([]);
    setStatusFilter("");
    dispatch(setFilteredCardAction([]));
  }, [shipment]);

  const handleFilter = (status: ValueOf<typeof SeaStatusesModel> | ValueOf<typeof AirStatusesModel>) => {
    if (status === statusFilter) return;
    dispatch(setFilteredCardAction([]));
    setStatusFilter(status);

    switch (shipment) {
      case ShipmentType.SEA:
        if (isSeaStatusModel(status)) {
          return seaHandleFilter(status);
        }
        return;
      case ShipmentType.AIR:
        if ((Object.values(AirStatusesDTO) as string[]).includes(status)) {
          return airHandleFilter(status as ValueOf<typeof AirStatusesDTO>);
        }
        return;
      default:
        return;
    }
  };

  const statusesLabelMap = {
    ...seaStatusLabelMap,
    ...airStatusLabelMap,
  };

  const refetchCards = () => {
    switch (shipment) {
      case ShipmentType.SEA:
        return refetchSeaCards();
      case ShipmentType.AIR:
        return refetchAirCards();
    }
  };

  const handleFilterAllBtnClick = () => {
    setStatusFilter("");
    resetFilter();
    refetchCards();
    dispatch(setFilteredCardAction([]));
  };

  const isShipmentTypeChanged = (): boolean => {
    if (!statusFilter) {
      return false;
    }
    if (shipment === ShipmentType.SEA) {
      const status = allStatuses.find((status) => status.status === statusFilter);

      if (status) {
        return false;
      }
      return true;
    }
    if (shipment === ShipmentType.AIR) {
      const status = allStatuses.find((status) => status.status === statusFilter);

      if (status) {
        return false;
      }
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isShipmentTypeChanged()) {
      handleFilterAllBtnClick();
    }
    if (!isCurrentViewOpenedCard && !isCurrentViewFilteredCards && !!statusFilter) {
      handleFilterAllBtnClick();
    }
  }, [shipment, currentView]);

  return (
    <div className={styles.statusFilterWrapper}>
      <ul className={styles.statusFilterList}>
        <li className={styles.statusFilterItem}>
          <Button
            data-test-id="status-all-button"
            className={cn(styles.statusFilterButton, { [styles.active]: !statusFilter.length })}
            onClick={() => {
              if (!statusFilter) return;
              handleFilterAllBtnClick();
              showMainView();
            }}
          >
            {t("All")} {allCardsLength}
          </Button>
        </li>
        {allStatuses.map((status) => (
          <li key={status.status} className={styles.statusFilterItem}>
            <Button
              data-test-id={`status-${status.status.toLowerCase()}-button`}
              className={cn(styles.statusFilterButton, { [styles.active]: statusFilter === status.status })}
              onClick={() => handleFilter(status.status)}
            >
              {t(statusesLabelMap[status.status])} <span className={styles.statusFilterCount}>{status.count}</span>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};
